<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { Footer } from '@/components/footer'

const earthVideo = ref<HTMLVideoElement | null>(null)
let pauseTimer: ReturnType<typeof setTimeout> | null = null

function startVideo() {
  const v = earthVideo.value
  if (!v) return
  if (pauseTimer) clearTimeout(pauseTimer)
  v.playbackRate = 0.08
  v.play()
    .then(() => { v.playbackRate = 0.08 })
    .catch((e: DOMException) => {
      if (!['AbortError', 'NotAllowedError', 'NotSupportedError'].includes(e.name)) throw e
    })
  pauseTimer = setTimeout(() => { earthVideo.value?.pause() }, 600_000)
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') startVideo()
  else {
    if (pauseTimer) { clearTimeout(pauseTimer); pauseTimer = null }
    earthVideo.value?.pause()
  }
}

onMounted(() => {
  startVideo()
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  if (pauseTimer) clearTimeout(pauseTimer)
})
</script>

<template>
  <div class="relative bg-black min-h-screen w-screen overflow-x-hidden">

    <div class="relative z-20 w-full max-w-[1700px] mx-auto px-6 xl:px-10 pt-8 pb-6 flex flex-col gap-6 xl:gap-x-6 xl:gap-y-6 xl:grid xl:grid-cols-[1fr_400px] xl:grid-rows-[auto_1fr_auto_auto] xl:h-screen">

      <div class="xl:col-span-2 xl:row-start-1 flex justify-center xl:justify-start py-[60px]">
        <router-link to="/" class="outline-none block">
          <img src="https://assets.earthpbc.com/earth-shield-400x100.png" alt="Earth" class="w-[140px] xl:w-54 h-auto aspect-[4/1]" />
        </router-link>
      </div>

      <div class="xl:col-start-1 xl:row-start-2 xl:flex xl:items-start">
        <div class="flex flex-col gap-3 xl:gap-5 max-w-[475px] mx-auto xl:mx-0">
          <h2 class="text-3xl xl:text-5xl font-bold text-white leading-tight tracking-wide">
            Welcome to EARTH
          </h2>
          <div>
            <p class="tracking-normal text-sm xl:text-base text-white/90 leading-relaxed">
              A global network of people working to protect forests, oceans, biodiversity, and human rights.
            </p>
            <ul class="tracking-normal mt-2 text-sm xl:text-base text-white/90 leading-relaxed space-y-1 list-disc list-outside pl-4">
              <li><span class="font-bold">Invitation-only</span> - Join a network of verified contributors</li>
              <li><span class="font-bold">Built for impact</span> – Public Benefit Corporation committed to people and planet over profit</li>
              <li><span class="font-bold">Satellite Monitoring</span> - Daily imaging and analysis of the Earth in collaboration with Planet.</li>
              <li><span class="font-bold">People and Projects</span> - Data systems for the field and office.</li>
              <li><span class="font-bold">GIS</span> - Maps and spatial analysis</li>
              <li><span class="font-bold">Digital payments</span> - Compliance and financial coordination in 100+ countries</li>
              <li><span class="font-bold">AI Reporting</span> - Instant reporting using Claude from Anthropic.</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="xl:col-start-2 xl:row-start-2 xl:flex xl:flex-col xl:justify-start max-w-sm xl:max-w-none mx-auto xl:mx-0 w-full">
        <slot />
      </div>

      <!-- Mobile: in-flow after card. Desktop: fixed fullscreen background. -->
      <video
        ref="earthVideo"
        class="earth-video object-contain pointer-events-none"
        style="filter: saturate(1.8) contrast(1.5) brightness(1.2)"
        loop muted playsinline
        poster="https://assets.earthpbc.com/earth-spin-1280.jpg"
      >
        <source src="https://assets.earthpbc.com/earth-spin.mp4" type="video/mp4">
      </video>

      <div class="xl:col-start-1 xl:row-start-3 xl:max-w-[300px] max-w-[500px] mx-auto xl:mx-0 w-full">
        <a href="https://partner.planet.com/directory/s/accountbrand/0TUHp00000008OmOAI/earth-pbc" target="_blank" rel="noopener noreferrer">
          <img src="https://assets.earthpbc.com/planet-orbit-partner-logo.svg" alt="planet.orbit – The Planet Partner Ecosystem" class="h-[60px] w-auto" />
        </a>
      </div>


      <div class="xl:col-span-2 xl:row-start-4 pointer-events-auto max-w-[500px] mx-auto xl:max-w-none xl:mx-0 w-full">
        <Footer transparent stacked />
      </div>

    </div>

  </div>
</template>

<style scoped>
.earth-video {
  width: 100%;
}

@media (min-width: 1280px) {
  .earth-video {
    position: fixed;
    inset: 0;
    width: 75%;
    height: 75%;
    margin: auto;
    z-index: -1;
  }
}

ul li::marker {
  color: #3b82f6; /* blue-500 */
}
</style>
